import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'
import { get } from '@vueuse/core'
import { type MaybeRef, computed } from 'vue'

import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import type {
	EmailLayout,
	EmailPreviewRecipient,
	EmailType,
} from '@/modules/companies/interfaces/emailSettings'

/**
 * HTML del correo de confirmación tal como sale, renderizado por el backend con el blade
 * real y datos de ejemplo.
 *
 * Se pide por axios y se inyecta con `srcdoc`, NO apuntando el iframe a la URL del
 * endpoint: el API vive en otro origen que la WebApp, así que un iframe hacia allá sería
 * cross-site y la cookie de sesión de Sanctum (SameSite=Lax) no viajaría — la previa
 * respondería 401. Por axios sí va con credenciales.
 *
 * `errorHandling: false` por lo mismo que el resto del módulo: sin eso el interceptor
 * global manda el 403/422 a una pantalla de error y saca al usuario de Mi empresa.
 */
const fetchPreview = async (
	companyId: number | string,
	emailType: EmailType,
	recipient: EmailPreviewRecipient,
	showMapLink: boolean,
	showPlatformBranding: boolean,
	showTravelerAmount: boolean,
	emailLayout: EmailLayout
): Promise<string> => {
	const { data } = await axios.get<{ html: string }>(
		`/api/companies/${companyId}/email-settings/preview`,
		{
			// El layout va SERIALIZADO: es un objeto anidado y axios lo mandaría como
			// emailLayout[marks][0][slot]=…, que el backend tendría que reconstruir. Un JSON
			// en un solo parámetro es más simple de leer y de validar allá.
			params: {
				emailType,
				recipient,
				showMapLink,
				showPlatformBranding,
				showTravelerAmount,
				emailLayout: JSON.stringify(emailLayout),
			},
			errorHandling: false,
		} as CustomAxiosRequestConfig
	)
	return data.html
}

const useCompanyEmailPreview = (
	companyId: MaybeRef<number | string | undefined>,
	emailType: MaybeRef<EmailType>,
	recipient: MaybeRef<EmailPreviewRecipient>,
	showMapLink: MaybeRef<boolean>,
	showPlatformBranding: MaybeRef<boolean>,
	showTravelerAmount: MaybeRef<boolean>,
	emailLayout: MaybeRef<EmailLayout>,
	enabled: MaybeRef<boolean> = true
) => {
	const resolvedId = computed(() => get(companyId))
	const resolvedType = computed(() => get(emailType))
	const resolvedRecipient = computed(() => get(recipient))
	const resolvedMapLink = computed(() => get(showMapLink))
	const resolvedBranding = computed(() => get(showPlatformBranding))
	const resolvedTravelerAmount = computed(() => get(showTravelerAmount))
	const resolvedLayout = computed(() => get(emailLayout))

	const { data, isLoading, isError, refetch } = useQuery({
		// El tipo y el destinatario van en la llave: cambiar cualquiera refresca el iframe
		// y las previas ya vistas quedan en caché.
		queryKey: computed(() => [
			'companyEmailPreview',
			resolvedId.value,
			resolvedType.value,
			resolvedRecipient.value,
			// Los flags van en la llave: mover un switch refresca el iframe al instante.
			resolvedMapLink.value,
			resolvedBranding.value,
			resolvedTravelerAmount.value,
			// El layout va serializado en la llave: es un objeto y vue-query compara por
			// referencia, así que mover una marca no refrescaría el iframe.
			JSON.stringify(resolvedLayout.value),
		]),
		queryFn: () =>
			fetchPreview(
				resolvedId.value!,
				resolvedType.value,
				resolvedRecipient.value,
				resolvedMapLink.value,
				resolvedBranding.value,
				resolvedTravelerAmount.value,
				resolvedLayout.value
			),
		// No se pide con plan insuficiente: el backend responde 422 y no hay nada que pintar.
		enabled: computed(() => !!resolvedId.value && get(enabled)),
		retry: false,
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000,
	})

	return {
		html: computed(() => data.value ?? ''),
		isLoading,
		isError,
		refetch,
	}
}

export default useCompanyEmailPreview
